// App.js
import { useState, useEffect } from "react";
import Loader from "./components/Loader";
import FloatingButtons from "./components/FloatingButtons";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const COMPONENT_LOAD_ORDER = [
  "Welcome",
  "Tagline",
  "CreationProcess",
  "Offerings",
  "DesignGallery",
  "AboutUs",
  "Partners",
  "Referral",
  "Contact",
];

function App() {
  const [fontLoaded, setFontLoaded] = useState(false);
  const [loadedComponents, setLoadedComponents] = useState({});
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [assetsLoaded, setAssetsLoaded] = useState({});

  // Font loading with guaranteed completion
  useEffect(() => {
    const loadFonts = async () => {
      try {
        await Promise.race([
          document.fonts.ready,
          new Promise((resolve) => setTimeout(resolve, 2000)),
        ]);
      } finally {
        setFontLoaded(true);
      }
    };
    loadFonts();
  }, []);

  // Component loading with reliable progress
  useEffect(() => {
    if (!fontLoaded) return;

    const totalSteps = COMPONENT_LOAD_ORDER.length * 2; // Component + assets
    let completedSteps = 0;

    const loadComponent = async (componentName) => {
      try {
        // Load component code
        const module = await import(`./components/${componentName}`);
        setLoadedComponents((prev) => ({
          ...prev,
          [componentName]: module.default,
        }));
        completedSteps++;
        setLoadingProgress(Math.min(95, (completedSteps / totalSteps) * 100));

        // Load component assets with timeout
        const assetsPromise = module.trackAssets?.() || Promise.resolve();
        await Promise.race([
          assetsPromise,
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);

        completedSteps++;
        setLoadingProgress(Math.min(95, (completedSteps / totalSteps) * 100));
        setAssetsLoaded((prev) => ({ ...prev, [componentName]: true }));
      } catch (error) {
        console.error(`Error loading ${componentName}:`, error);
        completedSteps += 2;
        setLoadingProgress((completedSteps / totalSteps) * 100);
      }
    };

    const loadAllComponents = async () => {
      await Promise.all(COMPONENT_LOAD_ORDER.map(loadComponent));

      // Final push to 100% with DOM check
      const images = Array.from(document.images);
      await Promise.all(
        images.map((img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              })
        )
      );

      // Force complete after all checks
      requestAnimationFrame(() => {
        setLoadingProgress(100);
        setTimeout(
          () => setAssetsLoaded((prev) => ({ ...prev, __final: true })),
          500
        );
      });
    };

    loadAllComponents();
  }, [fontLoaded]);

  const allLoaded =
    fontLoaded &&
    Object.keys(loadedComponents).length === COMPONENT_LOAD_ORDER.length &&
    assetsLoaded.__final;

  if (!allLoaded) {
    return <Loader percentage={Math.min(100, Math.floor(loadingProgress))} />;
  }

  return (
    <div>
      <FloatingButtons />
      <Navbar />
      <main>
        {COMPONENT_LOAD_ORDER.map((componentName) => {
          const Component = loadedComponents[componentName];
          return Component ? <Component key={componentName} /> : null;
        })}
      </main>
      <Footer />
    </div>
  );
}

export default App;
